// Example:
// <h2>
//   <button class="collapse-trigger" type="button">Click here to expand</button>
// </h2>
// <div class="collapse-content">
//   Content to be expanded
// </div>
//
// The trigger is a real button inside the heading, and the panel it controls is
// the element right after that heading. Either may carry `expanded` to start
// out open; `aria-expanded` and `aria-controls` are wired up here so consumer
// templates stay a heading and a panel.
//
// The collapsed height is the stylesheet's; every open height is written inline
// from here, because `max-height` only animates between concrete values.

const EXPANDED_CLASS = "expanded";
const CONTENT_CLASS = "collapse-content";
// An open panel is released rather than pinned to what it measured, so content
// that settles late — a web font swapping in, an image loading — is not clipped.
const UNBOUNDED = "none";

let panelCount = 0;

function setExpanded(trigger, panel, expanded, { animate = true } = {}) {
  trigger.classList.toggle(EXPANDED_CLASS, expanded);
  panel.classList.toggle(EXPANDED_CLASS, expanded);
  trigger.setAttribute("aria-expanded", String(expanded));

  if (!animate) {
    panel.style.maxHeight = expanded ? UNBOUNDED : "";
    return;
  }

  // Both directions need a measured height: opening animates towards it, and
  // closing animates away from it, since an open panel sits on `none`.
  panel.style.maxHeight = `${panel.scrollHeight}px`;
  if (!expanded) {
    // Read a layout value back so the browser commits that height before the
    // next line replaces it; without it the two writes coalesce into one.
    void panel.offsetHeight;
    panel.style.maxHeight = "";
  }
}

export function initExpandables() {
  for (const trigger of document.querySelectorAll(".collapse-trigger")) {
    const panel = trigger.parentElement.nextElementSibling;
    if (!panel?.classList.contains(CONTENT_CLASS)) {
      continue;
    }

    if (!panel.id) {
      panelCount += 1;
      panel.id = `collapse-content-${panelCount}`;
    }
    trigger.setAttribute("aria-controls", panel.id);
    setExpanded(trigger, panel, trigger.classList.contains(EXPANDED_CLASS), {
      animate: false,
    });

    panel.addEventListener("transitionend", (event) => {
      if (
        event.propertyName === "max-height" &&
        panel.classList.contains(EXPANDED_CLASS)
      ) {
        panel.style.maxHeight = UNBOUNDED;
      }
    });

    trigger.addEventListener("click", () => {
      setExpanded(trigger, panel, !trigger.classList.contains(EXPANDED_CLASS));
    });
  }
}
